import { api } from './client';

// Payment Types
export interface PaymentInitiationRequest {
  amount: number;
  currency: string;
  description: string;
  payerEmail: string;
  payerPhone?: string;
  paymentType: 'PROCESSING_FEE' | 'LOAN_REPAYMENT' | 'INSURANCE_FEE';
  clientLoanId?: number;
  /** Fineract loan account id (used for repayments from Loan Details) */
  fineractLoanId?: number;
  trackingNumber?: number;
  additionalInfo?: string;
  idempotencyKey?: string;
}

export interface PaymentInitiationResponse {
  id: number;
  paymentId: string;
  paynowReference: string;
  browserUrl: string;
  pollUrl: string;
  instructions: string;
  status: PaymentStatus;
  amount: number;
  hash: string;
}

export interface PaymentStatusResponse {
  id: number;
  paymentId: string;
  paynowReference: string;
  status: PaymentStatus;
  amount: number;
  reference: string;
  paynowStatus: string;
  hash: string;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentHistoryItem {
  id: number;
  paymentId: string;
  paynowReference: string;
  loanId: number;
  paymentType: string;
  amount: number;
  status: PaymentStatus | string;
  reference: string;
  paynowStatus: string;
  createdAt: string;
  updatedAt: string;
}

export interface LoanPaymentSummary {
  loanId: number;
  loanNumber?: string;
  currency?: string; // Loan currency (USD, ZIG, ZWG, etc.)
  // Fineract repayment schedule data
  nextInstallment?: {
    period: number;
    dueDate: string;
    principalDue: number;
    interestDue: number;
    totalDueForPeriod: number;
    complete: boolean;
  };
  arrearsStatus?: {
    isOverdue: boolean;
    daysOverdue?: number;
    overdueAmount?: number;
  };
  // Legacy fields (for backward compatibility)
  outstandingBalance?: number;
  processingFeePaid?: boolean;
  processingFeeAmount?: number;
  totalPaid?: number;
  lastPaymentDate?: string;
  nextPaymentDue?: string;
}

export interface LoanPaymentRequest {
  id: number;
  amount: number;
  currency: string;
  description: string;
  paymentType: string;
  dueDate?: string;
  status: string;
  isOutstanding: boolean;
  loanId?: string;
  trackingNumber?: string;
}

export type PaymentStatus = 
  | 'PENDING' 
  | 'INITIATED' 
  | 'AWAITING_DELIVERY' 
  | 'DELIVERED' 
  | 'PAID' 
  | 'CANCELLED' 
  | 'FAILED' 
  | 'DISPUTE'
  | 'DISPUTED'
  | 'REFUNDED';

export class PaynowService {
  private static mapPaymentEntityToHistoryItem(p: any, fallbackLoanId?: number): PaymentHistoryItem {

    
    const rawType = p?.paymentType;
    const typeStr = typeof rawType === 'string' ? rawType : (rawType?.toString?.() ?? 'LOAN_REPAYMENT');
    
    // Handle LocalDateTime from backend (convert to ISO string)
    const created = p?.initiatedAt ? 
      (typeof p.initiatedAt === 'string' ? p.initiatedAt : new Date(p.initiatedAt).toISOString()) :
      (p?.createdAt ? (typeof p.createdAt === 'string' ? p.createdAt : new Date(p.createdAt).toISOString()) :
      (p?.completedAt ? (typeof p.completedAt === 'string' ? p.completedAt : new Date(p.completedAt).toISOString()) :
      new Date().toISOString()));
    
    const updated = p?.completedAt ? 
      (typeof p.completedAt === 'string' ? p.completedAt : new Date(p.completedAt).toISOString()) :
      created;
    
    // Determine correct status - check backend fields
    let status = 'PENDING';
    if (p.successful === true) {
      status = 'PAID';
    } else if (p.failed === true) {
      status = 'FAILED';
    } else if (p.paymentStatus) {
      status = p.paymentStatus;
    }
    
    // Get reference - check backend fields
    const reference = p.paymentReference || p.paynowReference || '';
    
    
    
    const result = {
      id: p.id,
      paymentId: p.paymentReference || String(p.id),
      paynowReference: p.paynowReference || '',
      loanId: p.fineractLoanId || p.clientLoanId || fallbackLoanId || 0,
      paymentType: typeStr,
      amount: Number(p.amount || 0),
      status: status,
      reference: reference,
      paynowStatus: status,
      createdAt: created,
      updatedAt: updated,
    };
    

    return result;
  }

  // Helper method to process loan data
  private static processLoanData(loanData: any, loanId: number): LoanPaymentSummary {

    
    // Extract summary data from the loan response
    const summary = loanData.summary;
    const timeline = loanData.timeline;
    


    
    // Calculate days overdue
    const overdueSinceDate = summary?.overdueSinceDate;
    let daysOverdue = 0;
    

    
    if (overdueSinceDate && Array.isArray(overdueSinceDate) && overdueSinceDate.length === 3) {
      const overdueDate = new Date(overdueSinceDate[0], overdueSinceDate[1] - 1, overdueSinceDate[2]);
      const today = new Date();
      
      // Set time to start of day for accurate day calculation
      overdueDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      
      const timeDiff = today.getTime() - overdueDate.getTime();
      daysOverdue = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    } else {
      // Fallback: if we have totalOverdue > 0, estimate days overdue
      if (summary?.totalOverdue && summary.totalOverdue > 0) {
        // Rough estimate: assume 1 day per $10 overdue (this is just a fallback)
        daysOverdue = Math.max(1, Math.floor(summary.totalOverdue / 10));

      }
    }
    
    // Check if processing fee is paid
    const processingFeePaid = (summary?.feeChargesPaid || 0) > 0;
    const processingFeeAmount = summary?.feeChargesCharged || 0;
    




    
    // Get last payment date from transactions
    const transactions = loanData.transactions || [];
    const repaymentTransactions = transactions.filter((tx: any) => 
      tx.type?.code === 'loanTransactionType.repayment' || 
      tx.type?.code === 'loanTransactionType.repaymentAtDisbursement'
    );
    
    let lastPaymentDate: string | undefined;
    if (repaymentTransactions.length > 0) {
      const lastRepayment = repaymentTransactions[0]; // Assuming they're sorted by date
      if (lastRepayment.date && Array.isArray(lastRepayment.date) && lastRepayment.date.length === 3) {
        lastPaymentDate = new Date(lastRepayment.date[0], lastRepayment.date[1] - 1, lastRepayment.date[2]).toISOString();
      }
    }
    
    // Extract currency from loan data
    const currency = loanData.currency?.code || 
                     loanData.currencyCode || 
                     loanData.currency || 
                     summary?.currency?.code ||
                     summary?.currencyCode ||
                     'USD';

    const result = {
      loanId: loanId,
      loanNumber: loanData.accountNo || `LOAN-${loanId}`,
      currency: currency,
      // Use actual values from API
      outstandingBalance: summary?.totalOutstanding || 0,
      totalPaid: summary?.principalPaid || 0, // Only count principal payments, not fees
      processingFeePaid: processingFeePaid,
      processingFeeAmount: processingFeeAmount,
      lastPaymentDate: lastPaymentDate,
      // Arrears status
      arrearsStatus: {
        isOverdue: (summary?.totalOverdue || 0) > 0,
        daysOverdue: daysOverdue,
        overdueAmount: summary?.totalOverdue || 0
      },
      // Next installment (simplified - would need repayment schedule data)
      nextInstallment: timeline?.expectedMaturityDate ? {
        period: 1,
        dueDate: new Date(timeline.expectedMaturityDate[0], timeline.expectedMaturityDate[1] - 1, timeline.expectedMaturityDate[2]).toISOString(),
        principalDue: summary?.principalOutstanding || 0,
        interestDue: summary?.interestOutstanding || 0,
        totalDueForPeriod: summary?.totalOutstanding || 0,
        complete: false
      } : undefined
    };
    

    return result;
  }

  /**
   * Generate a unique idempotency key for payment requests
   * Combines user ID, timestamp, and random component
   */
  static generateIdempotencyKey(userId?: string): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 15);
    const userPart = userId ? userId.substring(0, 8) : 'anon';
    return `pay_${userPart}_${timestamp}_${random}`;
  }

  // Payment Initiation
  static async initiatePayment(request: PaymentInitiationRequest): Promise<PaymentInitiationResponse> {
    // Generate idempotency key if not provided
    if (!request.idempotencyKey) {
      // Try to get user ID from token or localStorage
      const userInfo = JSON.parse(localStorage.getItem('user_info') || '{}');
      const userId = userInfo.sub || userInfo.id || userInfo.userId || 'anonymous';
      request.idempotencyKey = this.generateIdempotencyKey(userId);
    }
    
    const response = await api.post('/api/v1/payments/initiate', request);
    return response.data;
  }

  // Check Payment Status
  static async getPaymentStatus(paymentReference: string): Promise<PaymentStatusResponse> {
    const response = await api.get(`/api/v1/payments/status/${paymentReference}`);
    return response.data;
  }

  // Get Payment History for User (normalized)
  static async getPaymentHistory(page: number = 0, size: number = 20): Promise<{
    content: PaymentHistoryItem[];
    totalPages: number;
    totalElements: number;
    size: number;
    number: number;
  }> {

    // Explicitly attach Authorization as a fallback in case a caller fires before interceptors run
    const token =
      localStorage.getItem('access_token') ||
      localStorage.getItem('kc_access_token') ||
      localStorage.getItem('keycloak_token') ||
      localStorage.getItem('token') ||
      sessionStorage.getItem('access_token') ||
      sessionStorage.getItem('kc_access_token') ||
      '';
    const response = await api.get(`/api/payments/history?page=${page}&size=${size}` , {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined
    });
    const raw = response.data || {};
    const rawContent = Array.isArray(raw.content) ? raw.content : [];
    
    // Map the raw backend data to PaymentHistoryItem format
    const content = rawContent.map((p: any) => this.mapPaymentEntityToHistoryItem(p));

    
    return {
      content,
      totalPages: Number(raw.totalPages || 1),
      totalElements: Number(raw.totalElements || content.length),
      size: Number(raw.size || size),
      number: Number(raw.number || page),
    };
  }

  // Poll Payment Status (for real-time updates)
  static async pollPaymentStatus(paymentReference: string): Promise<PaymentStatusResponse> {
    const response = await api.get(`/api/v1/payments/poll/${paymentReference}`);
    return response.data;
  }

  // Get active USD to ZiG exchange rate
  static async getActiveExchangeRate(): Promise<{ rate: number; pair: string }> {
    const response = await api.get('/api/rates/usd-to-zig');
    return response.data;
  }

  // Get today's USD to ZiG exchange rate (deprecated - use getActiveExchangeRate instead)
  static async getTodayExchangeRate(): Promise<{ rate: number; date: string; isToday: boolean; pair: string }> {
    const response = await api.get('/api/rates/usd-to-zig/today');
    return response.data;
  }

  // Loan-specific payment methods
  static async initiateLoanRepayment(loanId: number, amount: number): Promise<PaymentInitiationResponse> {
    const response = await api.post(`/api/loan-payments/${loanId}/repayment`, { amount });
    return response.data;
  }

  static async initiateProcessingFee(loanId: number): Promise<PaymentInitiationResponse> {
    const response = await api.post(`/api/loan-payments/${loanId}/processing-fee`);
    return response.data;
  }

  static async initiateInsuranceFee(loanId: number, amount: number): Promise<PaymentInitiationResponse> {
    const response = await api.post(`/api/loan-payments/${loanId}/insurance`, { amount });
    return response.data;
  }

  static async getLoanPaymentSummary(loanId: number): Promise<LoanPaymentSummary> {
    try {
      // Try the direct loan endpoint first since that's what we know works

      const response = await api.get(`/api/loans/${loanId}`);
      const loanData = response.data;
      

      
      // Check if we have the summary data
      if (loanData.summary) {

        return this.processLoanData(loanData, loanId);
      } else {

        // Fallback to associations endpoint
        const assocResponse = await api.get(`/api/loans/${loanId}/associations?associations=transactions`);
        const assocData = assocResponse.data;

        
        if (assocData.summary) {
          return this.processLoanData(assocData, loanId);
        }
      }
      
      // If no summary data found, return default

      return {
        loanId: loanId,
        loanNumber: `LOAN-${loanId}`,
        currency: 'USD',
        outstandingBalance: 0,
        processingFeePaid: false,
        processingFeeAmount: 0,
        totalPaid: 0
      };
    } catch (error) {
      console.error('Failed to fetch loan payment summary:', error);
      // Return a default summary if API call fails
      return {
        loanId: loanId,
        loanNumber: `LOAN-${loanId}`,
        currency: 'USD',
        outstandingBalance: 0,
        processingFeePaid: false,
        processingFeeAmount: 0,
        totalPaid: 0
      };
    }
  }

  static async getLoanPaymentHistory(loanId: number): Promise<PaymentHistoryItem[]> {
    // Now calls the new Fineract endpoint for transaction history with proper accounting
    const response = await api.get(`/api/fineract-loans/${loanId}/transaction-history`);
    return response.data;
  }

  // NEW: Get successful payments for a specific loan from local DB (Paynow-backed)
  static async getLocalSuccessfulPaymentsForLoan(loanId: number): Promise<PaymentHistoryItem[]> {
    const response = await api.get(`/api/v1/loans/${loanId}/payments/successful`);
    const data = response.data as any[];
    // Map backend Payment entity to PaymentHistoryItem
    return (Array.isArray(data) ? data : []).map((p: any) => this.mapPaymentEntityToHistoryItem(p, loanId));
  }

  static async getOutstandingBalance(loanId: number): Promise<{ balance: number }> {
    // This endpoint doesn't exist yet - will need to be implemented
    // For now, return a placeholder response
    return { balance: 0 };
  }

  // Get payment requests for a loan (proxied to external Loan Origination API)
  static async getLoanPaymentRequests(trackingNumber: string): Promise<LoanPaymentRequest[]> {
    try {
      const response = await api.get(`/api/v1/loan-origination/payment-requests/tracking/${trackingNumber}`);
      const data = response.data;

      const toClientShape = (item: any): LoanPaymentRequest => {
        const rawType = item?.paymentType || item?.payment_type || item?.paymentMethod || item?.payment_method || '';
        const normalizedType = String(rawType || '').toLowerCase();
        const inferredType = normalizedType || (String(item?.description || '').toLowerCase().includes('processing') ? 'processing_fee' : 'loan_repayment');
        return {
          id: item?.id ?? 0,
          amount: Number(item?.amount ?? 0),
          currency: item?.currency || 'USD',
          description: item?.description || 'Payment',
          paymentType: inferredType,
          dueDate: item?.dueDate || item?.due_date || undefined,
          status: item?.status || 'PENDING',
          isOutstanding: typeof item?.isOutstanding === 'boolean' ? item.isOutstanding : true,
          loanId: item?.loanId != null ? String(item.loanId) : undefined,
          trackingNumber: item?.trackingNumber != null ? String(item.trackingNumber) : undefined,
        };
      };

      if (Array.isArray(data)) return data.map(toClientShape);
      if (data && typeof data === 'object') return [toClientShape(data)];
      return [];
    } catch (error) {

      return [];
    }
  }

  // Get pending payment requests only (proxied to external Loan Origination API)
  static async getPendingLoanPaymentRequests(trackingNumber: string): Promise<LoanPaymentRequest[]> {
    try {
      const response = await api.get(`/api/v1/loan-origination/payment-requests/tracking/${trackingNumber}/pending`);
      const data = response.data;
      const toClientShape = (item: any): LoanPaymentRequest => {
        const rawType = item?.paymentType || item?.payment_type || item?.paymentMethod || item?.payment_method || '';
        const normalizedType = String(rawType || '').toLowerCase();
        const inferredType = normalizedType || (String(item?.description || '').toLowerCase().includes('processing') ? 'processing_fee' : 'loan_repayment');
        return {
          id: item?.id ?? 0,
          amount: Number(item?.amount ?? 0),
          currency: item?.currency || 'USD',
          description: item?.description || 'Payment',
          paymentType: inferredType,
          dueDate: item?.dueDate || item?.due_date || undefined,
          status: item?.status || 'PENDING',
          isOutstanding: typeof item?.isOutstanding === 'boolean' ? item.isOutstanding : true,
          loanId: item?.loanId != null ? String(item.loanId) : undefined,
          trackingNumber: item?.trackingNumber != null ? String(item.trackingNumber) : undefined,
        };
      };

      if (Array.isArray(data)) return data.map(toClientShape);
      if (data && typeof data === 'object') return [toClientShape(data)];
      return [];
    } catch (error) {

      return [];
    }
  }

  // Payment Statistics
  static async getPaymentStats(): Promise<{
    totalPayments: number;
    successfulPayments: number;
    failedPayments: number;
    totalAmount: number;
    successRate: number;
  }> {
    const response = await api.get('/api/payments/statistics');
    return response.data;
  }

  // Utility methods
  static getStatusColor(status: PaymentStatus): 'success' | 'warning' | 'error' | 'info' | 'default' {
    switch (status) {
      case 'PAID':
      case 'DELIVERED':
        return 'success';
      case 'PENDING':
      case 'INITIATED':
      case 'AWAITING_DELIVERY':
        return 'warning';
      case 'FAILED':
      case 'CANCELLED':
      case 'DISPUTED':
      case 'REFUNDED':
        return 'error';
      default:
        return 'default';
    }
  }

  static getStatusDisplayText(status: PaymentStatus): string {
    switch (status) {
      case 'PENDING':
        return 'Pending';
      case 'INITIATED':
        return 'Payment Initiated';
      case 'AWAITING_DELIVERY':
        return 'Awaiting Delivery';
      case 'DELIVERED':
        return 'Delivered';
      case 'PAID':
        return 'Paid Successfully';
      case 'CANCELLED':
        return 'Cancelled';
      case 'FAILED':
        return 'Payment Failed';
      case 'DISPUTE':
      case 'DISPUTED':
        return 'Disputed';
      case 'REFUNDED':
        return 'Refunded';
      default:
        return status;
    }
  }

  static formatPaymentType(type: any): string {
    // Normalize various shapes (enum object, string, undefined)
    let val: string = '';
    if (typeof type === 'string') {
      val = type;
    } else if (type && typeof type.name === 'string') {
      val = type.name;
    } else if (type && typeof type.value === 'string') {
      val = type.value;
    } else if (type && typeof type.toString === 'function') {
      val = type.toString();
    } else {
      val = '';
    }
    if (!val) return 'Payment';
    return val.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (l: string) => l.toUpperCase());
  }
}



